<template>
  <div class="p-0">
    <!-- Shared player tag portal for this page -->
    <SharedPlayerTagPortal @onToggleParticipate="toggleRoundsParticipated" />

    <b-loading :is-full-page="false" v-model="isLoading"></b-loading>

    <!-- Admin section -->
    <section class="panel is-danger" v-if="!isLoading">
      <p class="panel-heading">Admin Panel</p>

      <section class="is-flex is-justify-content-space-between my-3">
        <router-link to="/admin/tournaments" v-if="permission.admin">
          <a class="is-size-5 has-text-link"> &lt;&lt; Tournaments </a>
        </router-link>
        <span class="button is-static is-info is-light" style="border: none">
          <b>
            {{ tournament.name }}
            <span v-if="tournament.recurrence && tournament.recurrence.repeat">
              {{
                tournament.startTime
                  | asDayjsFormat(
                    `DD/MM/YYYY${
                      tournament.recurrence.repeat.basis === "minute"
                        ? " HH:mm"
                        : ""
                    }`
                  )
              }}
            </span>
          </b>
        </span>
        <b-button
          label="Edit Tournament"
          type="is-primary"
          @click="editTournament(tournament)"
        />
      </section>

      <!-- ANNOUCEMENT -->
      <b-field class="is-flex-grow-1">
        <b-input
          type="text"
          placeholder="Enter the message to broadcast to all players"
          expanded
          v-model="message"
        ></b-input>
        <b-button
          type="is-primary"
          label="Broadcast"
          @click="broadcast(message)"
        />
        <b-button type="is-danger" label="Clear" @click="broadcast('')" />
      </b-field>

      <!-- PLAYERS -->
      <div
        class="is-flex is-align-items-center is-justify-content-space-between"
      >
        <span class="is-size-5 has-text-info mx-2">
          {{ activePlayers.length }}
          {{ tournament.playerLimit ? "/" + tournament.playerLimit : "" }}
          PLAYERS
        </span>

        <span
          v-if="
            activePlayers.length &&
            activePlayers.length !== tournament.playerCount
          "
          class="has-text-danger mx-3"
        >
          Mismatched players count: {{ activePlayers.length }} vs
          {{ tournament.playerCount }}
          <button @click="updatePlayerCount">Update</button>
        </span>
        <div class="is-flex-grow-1 mx-5">
          <PlayersProgressBar :playersStats="playersStats" />
        </div>
        <div class="buttons">
          <b-button
            type="is-primary"
            icon-left="users"
            @click="isPlayersModalActive = true"
          >
            Edit Players
          </b-button>
          <b-button
            v-if="tournament.privacy === 'Paid'"
            type="is-warning"
            icon-left="id-card"
            :loading="isCheckingLicenses"
            @click="checkLicenses"
          >
            Update Licenses
          </b-button>
        </div>
      </div>

      <!-- ROUNDS -->
      <p class="is-size-5 has-text-left has-text-info mx-2 mt-5">
        {{ tournament.currentRound }}/{{ tournament.totalRounds }} ROUNDS:
      </p>
      <!-- Auto Schedule Round -->
      <div class="is-flex is-justify-content-space-between">
        <b-field>
          <b-button
            type="is-static"
            label="Tournament start"
            style="border: none"
          />
          <b-datetimepicker
            placeholder="Type or select a date..."
            editable
            icon="calendar"
            :disabled="sortedRounds[0] && !!sortedRounds[0].finishTime"
            :value="tournament ? tournament.startTime.toDate() : null"
            @input="(e) => setTournamentStartTime(e)"
          >
            <template #left>
              <b-button
                label="Now"
                type="is-primary"
                icon-left="clock"
                @click="setTournamentStartTime(new Date())"
              />
            </template>
          </b-datetimepicker>
        </b-field>

        <b-field>
          <b-button
            type="is-static"
            label="Round interval:"
            style="border: none"
          />
          <b-input
            style="width: 80px"
            type="number"
            :min="(tournament && tournament.baseTimeControl) || 15"
            v-model="roundInterval"
          ></b-input>
          <b-button type="is-static" label="minutes" style="border: none" />
          <b-button
            type="is-primary"
            label="Schedule"
            :disabled="tournament.currentRound >= tournament.totalRounds"
            @click="scheduleRounds"
          />
        </b-field>
      </div>

      <!-- Rounds admin -->
      <div
        v-for="round in sortedRounds.filter(
          (r) => r.roundNumber <= tournament.totalRounds
        )"
        :key="`Round${round.roundNumber}`"
      >
        <AdminRound
          :tournament="tournament"
          :rounds="sortedRounds"
          :round="round"
          :now="now"
          :roundGames="games.filter((g) => g.roundNumber === round.roundNumber)"
          @showRoundTable="showRoundTable(round.roundNumber)"
          @onStart="startRoundFromTable"
          @onFinish="finishRound"
          @onUnfinish="unfinishRound"
        />
      </div>

      <AdminReward :rankedPlayers="sortedPlayers" :tournament="tournament" />
    </section>

    <!-- Real Tournament section -->
    <p class="notification is-dark">
      Real tournament below for reference:
      <button @click="showTournament = !showTournament">
        {{ showTournament ? "Hide" : "Show" }}
      </button>
    </p>
    <div class="p-4">
      <Tournament v-if="showTournament" />
    </div>

    <section
      v-if="!isLoading && sortedRounds.length"
      style="position: fixed; top: 0; left: 0; z-index: 30"
    >
      <TournamentPipeline :tournament="tournament" :rounds="sortedRounds" />
    </section>

    <!-- Edit tournament Modal -->
    <b-modal
      v-model="isComponentModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      :can-cancel="false"
    >
      <template #default="props">
        <TournamentForm :tournament="formProps" @close="props.close" />
      </template>
    </b-modal>

    <!-- Edit Players Modal -->
    <b-modal
      v-model="isPlayersModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
    >
      <template #default="props">
        <AdminPlayersForm
          ref="adminPlayersForm"
          :tournament="tournament"
          :players="sortedPlayers"
          :playersStats="playersStats"
          @close="props.close"
        />
      </template>
    </b-modal>

    <!-- Round Table Modal -->
    <b-modal
      v-model="isRoundModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      width="auto"
    >
      <template #default="props">
        <div>
          <b-loading :is-full-page="false" :active="isRoundModalLoading" />
          <AdminRoundTable
            :tournament="tournament"
            :rounds="sortedRounds"
            :round="sortedRounds[modalRoundNumber - 1]"
            :games="games"
            :players="sortedPlayers"
            :elapsed="
              sortedRounds[modalRoundNumber - 1].startTime
                ? sortedRounds[modalRoundNumber - 1].finishTime >
                  sortedRounds[modalRoundNumber - 1].startTime
                  ? sortedRounds[modalRoundNumber - 1].finishTime -
                    sortedRounds[modalRoundNumber - 1].startTime
                  : (now -
                      sortedRounds[modalRoundNumber - 1].startTime.toDate()) /
                    1000
                : 0
            "
            @close="props.close"
            @onSave="updateStandings"
            @onStart="startRoundFromModal"
            @onFinalize="finalizeRound"
            @onFinish="finishRound"
            @onUnfinish="unfinishRound"
          />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import TournamentForm from "@/components/admins/TournamentForm.vue";
import AdminPlayersForm from "@/components/admins/AdminPlayersForm.vue";
import AdminRoundTable from "@/components/admins/AdminRoundTable.vue";
import PlayersProgressBar from "@/components/tournaments/PlayersProgressBar.vue";
import AdminReward from "@/components/admins/AdminReward.vue";
import Tournament from "@/views/Tournament.vue";
import TournamentPipeline from "@/components/admins/pipelines/TournamentPipeline.vue";
import AdminRound from "@/components/admins/AdminRound.vue";
import SharedPlayerTagPortal from "@/components/admins/SharedPlayerTagPortal.vue";
import { firestore } from "@/services/firebase";
import firebase from "firebase/app";
import TournamentPermission from "@/components/admins/TournamentPermission.js";
import {
  isPlayerInRound,
  toggleRoundsParticipated,
  pushRounds,
  scheduleRounds,
  saveStandings,
  updateCurrentRound,
  startRoundGames,
  finalizeRound,
  finishRound,
} from "vue-chess-shared/tournament/helper";
import { isAiPlayer } from "vue-chess-shared/utils/ai-helper";
import { GAME_OVERRIDE } from "vue-chess-shared/constants";
import MatchHelper from "@/utils/match-helper";
import { serverTimestamp } from "@/utils/firebase-helper";
import { TournamentsAPI } from "@/services/api";

export default {
  components: {
    Tournament,
    TournamentForm,
    AdminPlayersForm,
    AdminRoundTable,
    TournamentPipeline,
    PlayersProgressBar,
    AdminReward,
    AdminRound,
    SharedPlayerTagPortal,
  },
  name: "AdminTournament",
  title: "Admin Tournament",

  data() {
    return {
      isPageLoading: false,
      tournamentId: null,
      tournament: null,
      players: null,
      rounds: null,
      games: null,
      message: "",
      roundInterval: 30,
      isPlayersModalActive: false,
      isRoundModalActive: false,
      isRoundModalLoading: false,
      modalRoundNumber: 0,
      showTournament: false,
      timerInterval: null,
      now: null,
      isCheckingLicenses: false,
    };
  },

  mixins: [TournamentPermission],

  computed: {
    isLoading() {
      return (
        !this.tournament ||
        !this.players ||
        !this.rounds ||
        !this.games ||
        this.isPageLoading
      );
    },

    tournamentRef() {
      if (!this.tournamentId) return null;
      return firestore.collection("tournaments").doc(this.tournamentId);
    },

    activePlayers() {
      return this.players.filter((p) => !p.deletedAt);
    },

    sortedPlayers() {
      const roundNumber = Math.min(
        this.tournament.currentRound,
        this.tournament.totalRounds
      );

      return this.activePlayers
        .map((p) => ({
          ...p,
          isParticipating: isPlayerInRound(p, roundNumber),
          seed:
            p.user && p.user.stats && p.user.stats.rating
              ? p.user.stats.rating
              : 0,
          games: this.games.filter(
            (g) =>
              g.whiteUsername === p.username || g.blackUsername === p.username
          ),
        }))
        .sort((a, b) => b.score - a.score || b.seed - a.seed);
    },

    playersStats() {
      if (!this.sortedPlayers) return null;
      return {
        total: this.sortedPlayers.length,
        participating: this.sortedPlayers.filter((p) => p.isParticipating)
          .length,
        absent: this.sortedPlayers.filter((p) => !p.isParticipating).length,
      };
    },

    needUpdatePlayerCount() {
      //TODO: create a Cloud Funciton to handle aggregations
      if (!this.tournament) return false;
      if (!this.players) return false;
      if (!this.activePlayers.length) return false; // temp to avoid update to 0 at start
      return this.activePlayers.length !== this.tournament.playerCount;
    },

    sortedRounds() {
      if (!this.rounds) return [];
      return [...this.rounds].sort((a, b) => a.roundNumber - b.roundNumber);
    },
  },

  watch: {
    needUpdatePlayerCount: function () {
      if (this.needUpdatePlayerCount) {
        this.updatePlayerCount();
      }
    },
    permission() {
      this.checkPermission();
    },

    tournament() {
      this.checkPermission();
      if (
        this.tournament &&
        this.tournament.currentRound < this.tournament.totalRounds
      ) {
        this.checkLicenses();
      }
    },
  },

  methods: {
    broadcast(message) {
      if (this.message == null) this.message = this.tournament.message;
      else {
        this.tournamentRef.update({ message: message });
        this.message = message;
      }
    },

    updatePlayerCount() {
      console.log(
        `Update playerCount ${this.tournament.playerCount} -> ${this.activePlayers.length}`
      );
      this.tournamentRef.update({
        playerCount: this.activePlayers.length,
        playerUsernames: this.activePlayers.map((p) => p.username),
      });
    },

    toggleRoundsParticipated({ player, roundNumber }) {
      toggleRoundsParticipated(firestore, player, this.tournament, roundNumber);
    },

    setTournamentStartTime(date) {
      const startTime = firebase.firestore.Timestamp.fromDate(date);
      this.tournamentRef.update({
        startTime: startTime,
      });

      pushRounds(
        firestore,
        this.tournament,
        this.sortedRounds,
        0,
        (startTime - this.sortedRounds[0].startTime) / 60
      );
    },

    scheduleRounds() {
      scheduleRounds(firestore, this.tournament, this.rounds);
    },

    showRoundTable(roundNumber) {
      this.modalRoundNumber = roundNumber;
      this.isRoundModalActive = true;
    },

    async updateStandings(fromRoundNumber) {
      console.log("updateStandings", fromRoundNumber);
      const toRoundNumber = Math.min(
        this.tournament.currentRound,
        this.tournament.totalRounds
      );
      for (let i = fromRoundNumber; i <= toRoundNumber; i++) {
        await saveStandings(
          firestore,
          this.tournament,
          i,
          this.rounds,
          this.sortedPlayers,
          this.games.filter((g) => g.roundNumber <= i)
        );
      }
    },

    async startRoundFromTable(roundNumber) {
      this.isPageLoading = true;
      await this.startRound(roundNumber);
      this.isPageLoading = false;
    },

    async startRoundFromModal(roundNumber) {
      this.isRoundModalLoading = true;
      await this.startRound(roundNumber);
      this.isRoundModalLoading = false;
    },

    async startRound(roundNumber) {
      if (this.tournament.currentRound + 1 === roundNumber) {
        const startTime = firebase.firestore.Timestamp.fromDate(new Date());
        pushRounds(
          firestore,
          this.tournament,
          this.sortedRounds,
          roundNumber,
          (startTime - this.sortedRounds[roundNumber - 1].startTime) / 60
        );
      }

      updateCurrentRound(firestore, this.tournament, roundNumber);
      const { startedGames, failed, errors } = await startRoundGames(
        firestore,
        serverTimestamp(),
        this.tournament,
        roundNumber,
        this.activePlayers,
        this.games
      );

      if (failed > 0) {
        console.log("failed to start match", errors);
      }

      for (let i = 0; i < startedGames.length; i++) {
        const { game, tournament, whitePlayer, blackPlayer } = startedGames[i];
        console.log("To create a match", game.matchGUID);

        const matchId = await MatchHelper.createMatch({
          matchId: game.matchGUID,
          whitePlayer: whitePlayer.user,
          blackPlayer: blackPlayer.user,
          gameOverride:
            isAiPlayer(whitePlayer.user) || isAiPlayer(blackPlayer.user)
              ? GAME_OVERRIDE.PVAI
              : GAME_OVERRIDE.TOURNAMENT, // Tournament,
          duration: tournament.baseTimeControl * 60,
          increment: tournament.increment,
          variant: "standard",
          initialFen: tournament.initialFen,
          tournamentId: tournament.id,
          roundNumber: game.roundNumber,
          board: game.board,
        });

        if (!matchId) {
          console.log(
            tournament.id,
            game.roundNumber,
            "startRound",
            `Failed to start board ${game.board}`
          );
        }
      }
    },

    async finalizeRound(roundNumber) {
      this.isPageLoading = true;
      // TODO: fix this, as on frontend this.games[i].matchRef is not a ref, but automatically loaded data
      // but on backend, this.games[i].matchRef is actual ref
      const games = this.games.map((g) => {
        if (!g.matchRef) {
          return {
            ...g,
            id: g.id,
          };
        }
        const matchRef = firestore.collection("matches").doc(g.matchGUID);
        return {
          ...g,
          id: g.id,
          matchRef,
        };
      });
      await finalizeRound(firestore, this.tournament, roundNumber, games);
      this.isPageLoading = false;
    },

    async finishRound(roundNumber) {
      this.isPageLoading = true;
      const games = this.games.map((g) => {
        if (!g.matchRef) {
          return {
            ...g,
            id: g.id,
          };
        }
        const matchRef = firestore.collection("matches").doc(g.matchGUID);
        return {
          ...g,
          id: g.id,
          matchRef,
        };
      });
      const { logs } = await finishRound(
        firestore,
        serverTimestamp(),
        this.tournament,
        this.sortedRounds[roundNumber - 1],
        this.sortedRounds,
        this.sortedPlayers,
        games
      );
      console.log(logs);
      this.isPageLoading = false;
    },

    unfinishRound(roundNumber) {
      this.isPageLoading = true;
      if (
        roundNumber === this.tournament.totalRounds &&
        this.tournament.currentRound > this.tournament.totalRounds
      ) {
        this.tournamentRef.update({ currentRound: roundNumber }); // Un-finish tournament
      } else if (this.tournament.currentRound !== roundNumber) {
        // next round has started, can't un-finish this round
        return;
      }

      if (this.sortedRounds[roundNumber - 1].finishTime) {
        this.tournamentRef
          .collection("rounds")
          .doc(roundNumber + "")
          .update({
            finishTime: null,
          });
      }
      this.isPageLoading = false;
    },

    startTimer() {
      if (this.timerInterval) return;
      this.timerInterval = setInterval(() => {
        this.now = new Date();
      }, 1000);
    },

    stopTimer() {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    },

    async checkLicenses() {
      if (!this.tournament) {
        this.$buefy.toast.open({
          message: "Cannot check licenses - tournament data not loaded",
          type: "is-warning",
          duration: 5000,
        });
        return;
      }

      this.isCheckingLicenses = true;
      try {
        // Query db for all the licenses of players in tournament
        const subscriptions = await TournamentsAPI.getTournamentSubscriptions(
          this.tournament.id,
          this.tournament.planIds
        );

        // Add players to tournament
        const usernames = subscriptions.map((s) => s.username);
        if (usernames && usernames.length > 0) {
          if (this.tournament.currentRound < this.tournament.totalRounds) {
            // Get the AdminPlayersForm component reference
            const playersForm = this.$refs.adminPlayersForm;
            if (playersForm) {
              await playersForm.queryPlayers(usernames.join());
            } else {
              console.warn("AdminPlayersForm reference not found");
            }
          }
        }

        this.$buefy.toast.open({
          message: `License check completed. Found ${usernames.length} valid subscriptions.`,
          type: "is-success",
          duration: 5000,
        });
      } catch (error) {
        console.error("Error checking licenses:", error);
        this.$buefy.toast.open({
          message: "Failed to check licenses. Please try again.",
          type: "is-danger",
          duration: 5000,
        });
      } finally {
        this.isCheckingLicenses = false;
      }
    },
  },

  created() {
    this.tournamentId = this.$route.params.tournamentId;
    this.$bind("tournament", this.tournamentRef).then((tournament) => {
      this.roundInterval = tournament.baseTimeControl * 2 + 2;
    });
    this.$bind("players", this.tournamentRef.collection("players"));
    this.$bind("rounds", this.tournamentRef.collection("rounds"));
    this.$bind("games", this.tournamentRef.collection("games"));
    this.startTimer();
  },

  beforeDestroy() {
    this.stopTimer();
  },
};
</script>

<style scoped></style>
